import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Editorial theme colors
const COLORS = {
  cream: '#FAF8F5',
  charcoal: '#2D2A26',
  terracotta: '#C45D3A',
  border: '#2D2A26'
};

const SectionContainer = styled(Box)({
  backgroundColor: COLORS.cream,
  borderBottom: `1px solid ${COLORS.charcoal}`
});

const SectionHeader = styled(Box)({
  padding: '48px 64px 32px',
  borderBottom: `1px solid ${COLORS.charcoal}`,
  '@media (max-width: 768px)': {
    padding: '32px 20px 24px'
  }
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '42px',
  fontWeight: 700,
  color: COLORS.charcoal,
  letterSpacing: '-1px',
  '@media (max-width: 768px)': {
    fontSize: '32px'
  }
});

const SectionSubtitle = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: COLORS.charcoal,
  opacity: 0.6,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginTop: '8px'
});

const ContentGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  '@media (max-width: 968px)': {
    gridTemplateColumns: '1fr'
  }
});

const LocationColumn = styled(Box)({
  padding: '48px 64px',
  borderRight: `1px solid ${COLORS.charcoal}`,
  '@media (max-width: 968px)': {
    borderRight: 'none',
    borderBottom: `1px solid ${COLORS.charcoal}`,
    padding: '32px 20px'
  }
});

const HoursColumn = styled(Box)({
  padding: '48px 64px',
  '@media (max-width: 768px)': {
    padding: '32px 20px'
  }
});

const ColumnTitle = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '24px',
  fontWeight: 600,
  color: COLORS.charcoal,
  marginBottom: '24px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
});

const AddressText = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '16px',
  fontWeight: 400,
  color: COLORS.charcoal,
  lineHeight: 1.8,
  marginBottom: '24px'
});

const MapPlaceholder = styled(Box)({
  width: '100%',
  height: '250px',
  border: `1px solid ${COLORS.charcoal}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: `${COLORS.charcoal}08`
  }
});

const MapFrame = styled('iframe')({
  width: '100%',
  height: '100%',
  border: 'none'
});

const HoursTable = styled(Box)({
  display: 'flex',
  flexDirection: 'column'
});

const HoursRow = styled(Box)(({ isToday }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 0',
  borderBottom: `1px solid ${COLORS.charcoal}30`,
  backgroundColor: isToday ? `${COLORS.terracotta}15` : 'transparent',
  marginLeft: isToday ? '-16px' : 0,
  marginRight: isToday ? '-16px' : 0,
  paddingLeft: isToday ? '16px' : 0,
  paddingRight: isToday ? '16px' : 0,
  '&:last-child': {
    borderBottom: 'none'
  }
}));

const DayName = styled(Typography)(({ isToday }) => ({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: isToday ? 700 : 500,
  color: isToday ? COLORS.terracotta : COLORS.charcoal,
  textTransform: 'uppercase',
  letterSpacing: '1px'
}));

const HoursText = styled(Typography)(({ isToday }) => ({
  fontFamily: "'Space Mono', monospace",
  fontSize: '14px',
  fontWeight: isToday ? 700 : 400,
  color: isToday ? COLORS.terracotta : COLORS.charcoal
}));

const TodayBadge = styled(Box)({
  backgroundColor: COLORS.terracotta,
  color: COLORS.cream,
  padding: '4px 8px',
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '10px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  marginLeft: '8px'
});

const EditorialLocationHoursSection = ({ contactInfo, location }) => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const today = new Date().getDay();

  // Default business hours
  const defaultHours = {
    'Monday': '09:00 AM - 06:00 PM',
    'Tuesday': '09:00 AM - 06:00 PM',
    'Wednesday': '09:00 AM - 06:00 PM',
    'Thursday': '09:00 AM - 06:00 PM',
    'Friday': '09:00 AM - 06:00 PM',
    'Saturday': '10:00 AM - 04:00 PM',
    'Sunday': 'Closed'
  };

  // Parse hours from contactInfo or location
  const businessHours = contactInfo?.hours || location?.hours || defaultHours;

  const handleAddressClick = () => {
    if (contactInfo?.address) {
      const encodedAddress = encodeURIComponent(contactInfo.address);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
    }
  };

  // Generate map embed URL
  const getMapUrl = () => {
    const address = contactInfo?.address || '';
    if (address) {
      return `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
    }
    return null;
  };

  return (
    <SectionContainer id="location">
      <SectionHeader>
        <SectionSubtitle>Find Us</SectionSubtitle>
        <SectionTitle>Location & Hours</SectionTitle>
      </SectionHeader>

      <ContentGrid>
        <LocationColumn>
          <ColumnTitle>
            <LocationOnIcon sx={{ color: COLORS.terracotta }} />
            Address
          </ColumnTitle>

          {contactInfo?.address && (
            <AddressText>
              {contactInfo.address}
            </AddressText>
          )}

          <MapPlaceholder onClick={handleAddressClick}>
            {getMapUrl() ? (
              <MapFrame
                src={getMapUrl()}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <Typography
                sx={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: '14px',
                  color: COLORS.charcoal,
                  opacity: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '2px'
                }}
              >
                View on Map
              </Typography>
            )}
          </MapPlaceholder>
        </LocationColumn>

        <HoursColumn id="hours">
          <ColumnTitle>
            Business Hours
          </ColumnTitle>

          <HoursTable>
            {days.map((day, index) => {
              const isToday = index === today;
              const hours = typeof businessHours === 'object'
                ? (businessHours[day] || businessHours[day.toLowerCase()] || 'Hours not available')
                : 'Hours not available';

              return (
                <HoursRow key={day} isToday={isToday}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DayName isToday={isToday}>{day}</DayName>
                    {isToday && <TodayBadge>Today</TodayBadge>}
                  </Box>
                  <HoursText isToday={isToday}>{hours}</HoursText>
                </HoursRow>
              );
            })}
          </HoursTable>
        </HoursColumn>
      </ContentGrid>
    </SectionContainer>
  );
};

export default EditorialLocationHoursSection;
